import type { FacilityType } from "@klyra/shared";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isRtlLang } from "../lib/locale.ts";
import { navItemsFor } from "../lib/nav.ts";
import { useAuthStore } from "../stores/auth-store.ts";
import { DensityToggle } from "./DensityToggle.tsx";
import { LanguageToggle } from "./LanguageToggle.tsx";
import { NavItemBadge } from "./NavItemBadge.tsx";
import { ThemeToggle } from "./ThemeToggle.tsx";
import { Brand } from "./ui/Card.tsx";

interface MobileNavDrawerProps {
	open: boolean;
	onClose: () => void;
	onLogout: () => void;
	facilityType?: FacilityType | null;
}

/**
 * Slide-in nav drawer for mobile viewports (< sm).
 *
 * Why a separate component instead of a CSS-only hidden menu: on mobile
 * the staff need _all_ nav targets reachable with one tap. A hamburger
 * drawer with full-height list (48px tap targets) is what iOS/Android
 * users expect, and it works in both LTR/RTL because Tailwind's `end-*`
 * does the side-flip automatically.
 *
 * Locks body scroll while open so a long appointments list underneath
 * doesn't fight the user's thumb.
 */
export function MobileNavDrawer({
	open,
	onClose,
	onLogout,
	facilityType,
}: MobileNavDrawerProps) {
	const { t, i18n } = useTranslation();
	const routerState = useRouterState();
	const isRtl = isRtlLang(i18n.language);
	const role = useAuthStore((s) => s.clinic?.role);
	const hasPermission = useAuthStore((s) => s.hasPermission);
	const businessType = useAuthStore((s) => s.clinic?.business_type);
	const navItems = navItemsFor(facilityType, role, hasPermission, businessType);

	// Close drawer whenever the route changes. We don't trust the Link's
	// onClick alone because TanStack Router may complete navigation slightly
	// after the click event.
	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the intended trigger (close on route change); onClose is read fresh and must NOT itself re-run this effect
	useEffect(() => {
		onClose();
	}, [routerState.location.pathname]);

	// Lock scroll + ESC to close
	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = prev;
			document.removeEventListener("keydown", onKey);
		};
	}, [open, onClose]);

	if (!open) return null;

	const slideClass = isRtl
		? "right-0 animate-slide-in-rtl"
		: "left-0 animate-slide-in-ltr";

	return (
		<div
			className="fixed inset-0 z-50 sm:hidden"
			// biome-ignore lint/a11y/useSemanticElements: custom modal with scrim/panel + manual ESC/scroll-lock; a native <dialog> (showModal) would change focus/backdrop behavior
			role="dialog"
			aria-modal="true"
			aria-label={t("nav.menu")}
		>
			{/* Scrim */}
			<button
				type="button"
				onClick={onClose}
				aria-label={t("nav.close_menu")}
				className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
			/>
			{/* Panel */}
			<aside
				className={`absolute top-0 ${slideClass} h-full w-[82vw] max-w-[320px] bg-card shadow-2xl flex flex-col`}
				// Respect iPhone safe areas so the panel doesn't slide under the notch
				// or the home-indicator gesture bar.
				style={{
					paddingTop: "env(safe-area-inset-top)",
					paddingBottom: "env(safe-area-inset-bottom)",
				}}
			>
				<div className="flex items-center justify-between px-4 py-4 border-b border-border">
					<Brand />
					<button
						type="button"
						onClick={onClose}
						aria-label={t("nav.close_menu")}
						className="size-11 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted active:bg-border dark:active:bg-gray-700 text-2xl leading-none"
					>
						×
					</button>
				</div>

				<nav className="flex-1 overflow-y-auto py-2">
					{navItems.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							onClick={onClose}
							className="flex items-center gap-3 px-4 py-3.5 text-base text-foreground hover:bg-muted active:bg-muted dark:active:bg-gray-800 min-h-[48px]"
							activeProps={{
								className:
									"flex items-center gap-3 px-4 py-3.5 text-base bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300 font-semibold border-s-4 border-brand-500 min-h-[48px]",
							}}
						>
							<item.icon
								className="size-5 shrink-0"
								strokeWidth={2}
								aria-hidden
							/>
							<span>{t(item.i18nKey)}</span>
							<NavItemBadge item={item} />
						</Link>
					))}
				</nav>

				<div className="border-t border-border p-3 flex flex-col gap-2">
					<div className="flex items-center justify-center gap-2 flex-wrap">
						<ThemeToggle />
						<LanguageToggle />
						<DensityToggle />
					</div>
					<button
						type="button"
						onClick={() => {
							onClose();
							onLogout();
						}}
						className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-muted hover:bg-border active:bg-gray-300 dark:active:bg-gray-600 text-foreground font-medium text-sm"
					>
						<LogOut
							className="size-4 rtl:rotate-180"
							strokeWidth={2}
							aria-hidden
						/>
						{t("nav.logout")}
					</button>
				</div>
			</aside>
		</div>
	);
}

export function HamburgerButton({
	onClick,
	ariaLabel,
}: {
	onClick: () => void;
	ariaLabel: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={ariaLabel}
			className="size-11 rounded-md flex items-center justify-center text-foreground hover:bg-muted active:bg-border dark:active:bg-gray-700 sm:hidden"
		>
			<svg
				width="22"
				height="22"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				aria-hidden="true"
				focusable={false}
			>
				<line x1="3" y1="6" x2="21" y2="6" />
				<line x1="3" y1="12" x2="21" y2="12" />
				<line x1="3" y1="18" x2="21" y2="18" />
			</svg>
		</button>
	);
}
